import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { BookingModal } from "@/components/BookingModal";
import { Star, MapPin, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Hotels() {
  const { t, lang, money } = useApp();
  useSeo({ title: t.seo.hotels[0], description: t.seo.hotels[1] });
  const [params] = useSearchParams();
  const city = params.get("city");
  const stay = { check_in: params.get("check_in") || "", check_out: params.get("check_out") || "", rooms: params.get("rooms") || 1, adults: params.get("adults") || 2,
    children_ages: (params.get("children") || "").split(",").filter(Boolean).map(Number) };
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.post("/hotels/search", { city: city || null }).then((r) => setHotels(r.data)).finally(() => setLoading(false));
  }, [city]);

  return (
    <div className="bg-sand min-h-screen">
      <div className="bg-tigris pt-8 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-white text-2xl sm:text-3xl font-bold mb-6 font-display">{t.nav.hotels}</h1>
          <HeroSearch />
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-lg font-bold text-slate-700 mb-5">{t.hotels.results}</h2>
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-tigris" size={40} /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotels.map((h, i) => (
              <motion.div key={h.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                data-testid={`hotel-card-${h.id}`}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-hover flex flex-col">
                <div className="relative h-48">
                  <img src={h.image} alt={h[`name_${lang}`]} className="w-full h-full object-cover" />
                  <span className="absolute top-3 end-3 bg-white/90 text-tigris text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <Star size={12} className="fill-gold text-gold" /> {h.rating}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-0.5 mb-1.5">
                    {Array.from({ length: h.stars }).map((_, k) => <Star key={k} size={13} className="fill-gold text-gold" />)}
                  </div>
                  <h3 className="font-bold text-base text-slate-800 mb-1">{h[`name_${lang}`]}</h3>
                  <p className="text-xs text-slate-400 flex items-center gap-1 mb-3"><MapPin size={12} /> {h[`city_${lang}`]}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4 flex-1">
                    {h[`amenities_${lang}`].map((a, k) => (
                      <span key={k} className="text-[11px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{a}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-extrabold text-tigris">{money(h.price)}</span>
                      <span className="text-xs text-slate-400">{t.common.perNight}</span>
                    </div>
                    <button data-testid={`book-hotel-${h.id}`} onClick={() => setBooking({ type: "hotel", item_id: h.id, title: h[`name_${lang}`], amount: h.price, details: { city: h[`city_${lang}`], ...stay } })}
                      className="btn-primary px-5 py-2.5 rounded-xl font-bold text-sm">{t.common.bookNow}</button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      {booking && <BookingModal item={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}
